import { Link } from "react-router-dom";
import {
  ArrowUpRight, KeyRound, IndianRupee, ClipboardCheck, Paintbrush, Wrench, FileCheck, Sofa,
} from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Stagger, StaggerItem } from "@/components/Reveal";
import { track } from "@/lib/analytics";

const SERVICES = [
  {
    Icon: KeyRound,
    title: "Rental & Tenant Management",
    copy: "Tenant search, verification, agreements, renewals, move-ins, move-outs and every issue in between.",
    href: "/rental-management",
    featured: true,
  },
  {
    Icon: IndianRupee,
    title: "Rent Collection & Reporting",
    copy: "Due dates tracked, tenants reminded, records maintained, owner updated — every month.",
    href: "/rent-collection",
  },
  {
    Icon: ClipboardCheck,
    title: "Property Inspection",
    copy: "Periodic, move-in, move-out and damage inspections with photo and video evidence.",
    href: "/property-inspection",
  },
  {
    Icon: Paintbrush,
    title: "Make-Ready & Re-Rental",
    copy: "Tenant vacated? Inspection, condition report, repairs, cleaning, painting and a new tenant.",
    href: "/property-make-ready",
  },
  {
    Icon: Wrench,
    title: "Maintenance & Repairs",
    copy: "Plumbing, electrical, carpentry, AC, civil works, cleaning and pest control — one contact.",
    href: "/property-maintenance",
  },
  {
    Icon: FileCheck,
    title: "Documentation & Compliance",
    copy: "Rental agreements, verification assistance, utility and society coordination, digital records.",
    href: "/rental-agreement-assistance",
  },
  {
    Icon: Sofa,
    title: "Interior & Renovation",
    copy: "Design, modular work, false ceiling, flooring and complete execution for larger projects.",
    href: "/interior-services",
  },
];

export const ServicesBento = () => (
  <section className="bg-ivory py-24 sm:py-32" data-testid="services-overview-section">
    <div className="container-site">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading
          chapter="03"
          eyebrow="What We Handle"
          title="One Team for the Entire Life of Your Property."
        />
        <Link
          to="/services"
          className="btn-outline-navy"
          data-testid="services-overview-all"
          onClick={() => track("service_enquiry", { location: "services_overview" })}
        >
          View All Services <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" gap={0.07}>
        {SERVICES.map(({ Icon, title, copy, href, featured }) => (
          <StaggerItem key={href} className={featured ? "sm:col-span-2" : ""}>
            <Link
              to={href}
              data-testid={`service-card-${href.slice(1)}`}
              onClick={() => track("service_enquiry", { service: title })}
              className={`card-lift group flex h-full flex-col justify-between rounded-2xl border p-7 sm:p-8 ${
                featured ? "border-navy-900 bg-navy-900 text-white" : "border-navy-900/10 bg-white"
              }`}
            >
              <div>
                <div className="flex items-start justify-between">
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                      featured ? "bg-gold-500/15 text-gold-400" : "bg-ivory text-navy-900"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <ArrowUpRight
                    className={`h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 ${
                      featured ? "text-gold-400" : "text-navy-900/30 group-hover:text-gold-600"
                    }`}
                  />
                </div>
                <h3 className={`mt-6 font-display text-xl font-extrabold tracking-tight sm:text-2xl ${featured ? "text-white" : "text-navy-900"}`}>
                  {title}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-white/65" : "text-muted-foreground"}`}>
                  {copy}
                </p>
              </div>
              <span className={`mt-6 font-display text-xs font-bold uppercase tracking-[0.22em] ${featured ? "text-gold-400" : "text-gold-600"}`}>
                Explore service
              </span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);
